import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Control, useFormContext } from 'react-hook-form'
import Button from '../../Button/Button'
import Icon from '../../Icon/Icon'
import Link from '../../Link/Link'
import PhoneInput from '../PhoneInput/PhoneInput'
import TextInput from '../TextInput/TextInput'

interface ContactInputProps {
  contactType: 'origin' | 'destination'
  control: Control
  defaults: [string, string]
  name: string
  onSave?: (attentionName: string, phoneNumber: string) => void
  note: string
}

export default function ContactInput({
  contactType,
  control,
  defaults,
  name,
  onSave,
  note,
}: ContactInputProps): React.JSX.Element {
  const [contact, setContact] = useState<[string, string]>(defaults)
  const [editMode, setEditMode] = useState(false)
  const formContext = useFormContext()

  // Handle case where useFormContext returns null
  const getValues = useMemo(
    () => formContext?.getValues || (() => ['', '']),
    [formContext],
  )
  const setValue = useMemo(
    () => formContext?.setValue || (() => {}),
    [formContext],
  )

  const isValid = useMemo(() => contact.every((value) => !!value), [contact])

  const onChange = useCallback(
    (index: number) => (value?: string | number) =>
      index === 0
        ? setContact([value?.toString() || '', contact[1]])
        : setContact([contact[0], value?.toString() || '']),
    [contact, setContact],
  )

  const onContactSave = useCallback(() => {
    const [attentionName, phoneNumber] = getValues([
      `${name}.attention_name`,
      `${name}.phone_number`,
    ])

    onSave?.(attentionName, phoneNumber)
    setContact([attentionName, phoneNumber])
    setEditMode(false)
  }, [getValues, name, onSave])

  useEffect(() => {
    const [attentionName, phoneNumber] = defaults

    setContact([attentionName, phoneNumber])
    setValue(`${name}.attention_name`, attentionName, { shouldDirty: false })
    setValue(`${name}.phone_number`, phoneNumber, { shouldDirty: false })
  }, [defaults, name, setValue])

  return (
    <div className="mt-4 flex flex-col gap-y-4 w-full bg-white p-6 rounded-lg">
      {!editMode ? (
        <div className="flex flex-col gap-4 mb-2">
          {/* Attention name and phone number */}
          <div className="flex w-full justify-between">
            <div className="flex gap-6">
              <div className="flex flex-col gap-2">
                <div className="type-body-5">
                  {`${contactType === 'origin' ? 'Sender' : 'Recipient'} Name`}
                </div>
                <div>{contact?.[0]}</div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="type-body-5">Phone Number</div>
                <div>{contact?.[1]}</div>
              </div>
            </div>
            {/* Edit attention name link */}
            <div className="flex items-start gap-2">
              <Link
                className="h-auto !px-0"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault()
                  setEditMode(true)
                }}
              >
                Edit
              </Link>
            </div>
          </div>
          {/* Shipping label info */}
          <div className="flex items-center gap-2">
            <Icon icon="info-circle-filled" className="w-5 h-5" />
            <div className="type-legal-1-desktop">{note}</div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-4 w-full">
            <TextInput
              className="flex-1"
              control={control}
              defaultValue={defaults[0]}
              label={`${contactType === 'origin' ? 'Sender' : 'Recipient'} name*`}
              name={name + '.attention_name'}
              onChange={onChange(0)}
              placeholder="Jane Doe"
              maxLength={35}
              rules={{
                required: { value: true, message: 'Name is required' },
                maxLength: {
                  value: 35,
                  message: 'Name must be 35 characters or less',
                },
              }}
            />
            <PhoneInput
              className="flex-1"
              control={control}
              defaultValue={defaults[1]}
              label="Phone number*"
              name={name + '.phone_number'}
              onChange={onChange(1)}
              placeholder="555-555-5555"
              rules={{
                required: { value: true, message: 'Phone number is required' },
              }}
            />
          </div>
          {/* combine icon and label in one row */}
          <div className="flex items-center gap-2">
            <Icon icon="info-circle-filled" className="w-5 h-5" />
            <div className="type-legal-1-desktop">{note}</div>
          </div>
          <Button
            className="w-[120px]"
            onClick={onContactSave}
            type="button"
            disabled={!isValid}
          >
            Save
          </Button>
        </>
      )}
    </div>
  )
}
